import React from 'react';
import { useSearchEmployeeList } from './hooks/useSearchEmployeeList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import SearchEmployeeSummaryCards from './components/SearchEmployeeSummaryCards';
import SearchEmployeeTable from './components/SearchEmployeeTable';

export default function SearchEmployeeListScreen() {
  const { state, actions } = useSearchEmployeeList();

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Search Employee
          </h1>
          <p className="mt-1.5 text-xs text-on-surface-variant font-medium">
            Company-wide workforce directory, branch locations, and staff lookup
          </p>
        </div>

        {/* Status Toggle Capsule */}
        <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs select-none">
          {state.statusOptions.map((tab) => {
            const isActive = state.statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => actions.setStatusFilter(tab.value)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  isActive
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Summary KPI Cards */}
      <SearchEmployeeSummaryCards summary={state.summary} />

      {/* Shared Filters Component */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        showStatus={false}

        branchFilter={state.branchFilter}
        branchOptions={state.branchOptions}
        departmentFilter={state.departmentFilter}
        departmentOptions={state.departmentOptions}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}

        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}

        searchFieldOptions={state.searchFieldOptions}

        visibleAccordionFilters={[
          'branch',
          'department',
        ]}

        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Employee Search Table */}
      <section className="space-y-4">
        <SearchEmployeeTable
          records={state.records}
          isLoading={state.isLoading}
          onResetFilters={actions.resetFilters}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}
